import DashboardLayout from "components/layouts/dashboard-layout";
import PageLinks from "components/page-links";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { GrayBody, GrayCard, GrayHeader } from "../../components/style";
import BadgeIcon from "components/badgeIcon";
import Typography from "components/typography";
import { Colors } from "utils/colors";
import PrimaryButton from "components/buttons/primary-button";
import { useNavigate } from "react-router-dom";
import TextInput from "components/inputs/text-input";

import Dropdown from "components/inputs/dropdown";
import { Countries } from "constants/countries";
import Modal from "components/modal";
import ModalTemplate from "components/modal/modal-template";
import VerifyEmail from "components/modal/modals/verifyEmail";

import api from "services/axiosInstance";
import Toast from "components/toast";
import { IUser } from "types/user";
import { GetUser } from "services/user/getUser";
import { SetUser } from "services/user/setUser";

const SettingProfile = () => {
  const navigate = useNavigate();
  const [isModalActive, setIsModalActive] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verified, setVerified] = useState(false);

  const UserData = GetUser();

  const [fullName, setFullName] = useState(UserData?.name);
  const [email, setEmail] = useState(UserData?.email);
  const [country, setCountry] = useState(UserData?.country || "");
  const [phoneNumber, setPhoneNumber] = useState("");

  const [newEmail, setNewEmail] = useState("");
  const [verifyNewEmail, setVerifyNewEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const SendVerificationCode = async () => {
    try {
      setVerificationSent(true);
    } catch (error) {
      Toast({ error });
    } finally {
    }
  };

  const Verify = async (otp: string) => {
    try {
      setVerified(true);
    } catch (error) {
    } finally {
    }
  };

  const UpdateEmail = async () => {
    try {
      setIsModalActive(false);
      setVerificationSent(false);
      setVerified(false);
    } catch (error) {
    } finally {
    }
  };

  const SaveChanges = async () => {
    try {
      setSaving(true);

      const res = await api.patch("users/profile", {
        name: fullName,
        email,
        country
      });

      SetUser(res.data);
      Toast({
        type: "success",
        message: `Profile has been updated successfully`
      });
    } catch (error) {
      Toast({ error });
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <>
        <PageLinks
          from={{ name: "Settings", link: "/settings" }}
          current="Profile"
        />
        <GrayCard>
          <GrayHeader>
            <NameContainer>
              <BadgeIcon
                name={UserData?.name || ""}
                size="6.4"
                fontSize="2.4rem"
                bgColor={Colors.purple200}
                m_size="4.8"
              />
              <NameArtist>
                <Typography
                  color={Colors.white100}
                  size="2xl"
                  weight="medium"
                  mb="0.4"
                  m_size="xl"
                >
                  {UserData?.name}
                </Typography>
                <Typography color={Colors.grey500} m_size="s">
                  {UserData?.artistProfiles?.length} Artist
                  {UserData?.artistProfiles &&
                  UserData?.artistProfiles?.length > 0
                    ? "s"
                    : ""}
                </Typography>
              </NameArtist>
            </NameContainer>

            <PrimaryButton
              onClick={() => {
                navigate(-1);
              }}
              text="CANCEL"
              fitContent
              variant="border"
              textColor={Colors.white100}
            />
          </GrayHeader>
          <GrayBody>
            <InputGrid>
              <TextInput
                label="Full Name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
              <EmailInputContainer>
                <TextInput
                  label="Email"
                  value={email}
                  disabled
                  lockIcon={false}
                />
                <PrimaryButton
                  variant="text"
                  text="Verify to change your email address"
                  fitContent
                  textColor={Colors.yellow400}
                  textSize="xs"
                  onClick={() => setIsModalActive(true)}
                />
              </EmailInputContainer>
              <Dropdown
                label="Country"
                chosenItem={country}
                selectItem={(item) => setCountry(item)}
                items={Countries.map((c) => c.country)}
              />
              <TextInput
                label="Phone Number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
              />
            </InputGrid>
            <BottomButton>
              <PrimaryButton
                onClick={SaveChanges}
                text="Save Changes"
                loading={saving}
              />
            </BottomButton>
          </GrayBody>
        </GrayCard>
        <Modal
          isActive={isModalActive}
          closeModal={() => setIsModalActive(false)}
        >
          {!verificationSent && !verified ? (
            <ModalTemplate
              close={() => setIsModalActive(false)}
              twoButtons
              confirmText="Send Verification Code"
              cancelText="Cancel"
              header="Verify Your Email Address"
              confirm={SendVerificationCode}
            >
              <Typography weight="medium" color={Colors.white300} mb="2.4">
                We’ll need to verify your email address, {email}, in order to
                change it
              </Typography>
              <Typography weight="medium" color={Colors.white300}>
                Lost access to your email? Please contact your email provider to
                regain access
              </Typography>
            </ModalTemplate>
          ) : verificationSent && !verified ? (
            <VerifyEmail
              close={() => setIsModalActive(false)}
              confirm={(otp) => {
                Verify(otp);
              }}
              resend={SendVerificationCode}
            />
          ) : verificationSent && verified ? (
            <ModalTemplate
              close={() => setIsModalActive(false)}
              confirmText="Done"
              header="Enter New Email"
              confirm={UpdateEmail}
            >
              <NewEmailInputs>
                <Typography align="center" color={Colors.white300}>
                  Enter a new email address and verify the email address by
                  re-entering it.
                </Typography>
                <TextInput
                  label="Email"
                  value={newEmail}
                  placeholder="example@gmail.com"
                  onChange={(e) => setNewEmail(e.target.value)}
                />
                <TextInput
                  label="Verify Email"
                  value={verifyNewEmail}
                  placeholder="example@gmail.com"
                  onChange={(e) => setVerifyNewEmail(e.target.value)}
                />
              </NewEmailInputs>
            </ModalTemplate>
          ) : (
            <></>
          )}
        </Modal>
      </>
    </DashboardLayout>
  );
};

export default SettingProfile;

const NameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1.6rem;
`;

const NameArtist = styled.div``;

const InputGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 1.6rem;
  grid-row-gap: 2.4rem;
  @media only screen and (max-width: 769px) {
    grid-template-columns: 1fr;
  }
`;

const EmailInputContainer = styled.div`
  width: 100%;
`;
const BottomButton = styled.div`
  margin-top: 2.4rem;
  width: fit-content;
  margin-left: auto;
  @media only screen and (max-width: 769px) {
    width: 100%;
  }
`;

const NewEmailInputs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.4rem;
  width: 100%;
`;
